import * as Sentry from "@sentry/react";
import * as z from "zod";

import type { Error as ApiError } from "~/api/Api";

const isApiError = (value: unknown): value is ApiError => {
  return (
    typeof value === "object" &&
    value !== null &&
    "code" in value &&
    "message" in value
  );
};

export const messageFromError = (error: unknown) => {
  // If already normalized ApiError
  if (isApiError(error)) {
    return error.validation_messages?.length
      ? error.validation_messages.join(" ")
      : `${error.message} (${error.code})`;
  }

  // If raw fetch Response
  if (error instanceof Response) {
    return messageFromResponse(error);
  }

  // Native JS error
  if (error instanceof Error) {
    return `An unexpected error occurred: ${error.message}`;
  }

  // Completely unknown
  Sentry.captureException(
    new Error(
      `An unknown error occurred: ${JSON.stringify(error).slice(0, 1000)}`,
    ),
  );

  return "An unknown error occurred";
};

const ApiErrorDataSchema = z.object({
  error: z.object({
    code: z.number(),
    message: z.string(),
    validation_messages: z.array(z.string()).optional(),
  }),
});

type ResponseWithError = Response & {
  error: z.infer<typeof ApiErrorDataSchema> | Error;
};

const isResponseWithError = (
  response: unknown,
): response is ResponseWithError => {
  return response instanceof Response && "error" in response;
};

const messageFromResponse = (response: Response) => {
  if (!isResponseWithError(response)) {
    return `An unexpected server or network error occurred: ${response.statusText} (${response.status})`;
  }

  if (response.error instanceof Error) {
    return `An unexpected server or network error occurred: ${response.error.message}`;
  }

  const { error } = ApiErrorDataSchema.parse(response.error);

  return error.validation_messages?.length
    ? error.validation_messages.join(" ")
    : `${error.message} (${error.code})`;
};
